<template>
  <v-card-text>
    <div class="d-flex flex-column flex-sm-row">
      <v-card class="d-flex flex-column flex-1 mr-sm-2">
        <v-card-title>Devices</v-card-title>
        <v-divider></v-divider>
        <v-chart class="chart" :option="devicesChart" />
      </v-card>
      <v-card class="d-flex flex-column flex-1 ml-sm-2">
        <v-card-title>Operations</v-card-title>
        <v-divider></v-divider>
        <v-chart class="chart" :option="operationsChart" />
      </v-card>
    </div>

    <!-- Initialization Loading/Error States -->
    <v-row v-if="!isSearchReadyAndInitialized" class="mt-5">
      <v-col cols="12">
        <v-alert v-if="initializationError" type="error" prominent>
          <strong>Error initializing search:</strong> {{ initializationError }}
          <p>Please check the console for more details.</p>
        </v-alert>
        <div v-else class="text-center">
          <v-progress-linear
            v-if="isInitializing && totalRecordsToProcess > 0"
            :value="(recordsProcessed / totalRecordsToProcess) * 100"
            color="primary" height="25" class="mb-2" stream rounded
          >
            <template v-slot:default="{ value }">
              <strong>{{ Math.ceil(value) }}% ({{ initializationStatus }})</strong>
            </template>
          </v-progress-linear>
          <v-progress-circular
            v-if="isInitializing && totalRecordsToProcess === 0"
            indeterminate color="primary" size="64" width="6" class="mb-2"
          ></v-progress-circular>
          <p v-if="isInitializing" class="mt-1">Please wait...</p>
          <p v-else class="mt-1">Search system preparing...</p>
        </div>
      </v-col>
    </v-row>

    <!-- Filters UI -->
    <v-card v-if="isSearchReadyAndInitialized" class="my-5 pa-3" outlined>
      <v-card-title class="pa-0 mb-2">Filter By</v-card-title>
      <v-row dense>
        <!-- <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.device" label="Device (contains)"
            @input="debouncedSearch" clearable outlined dense hide-details="auto"
          />
        </v-col> -->
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.device_cleaned" label="Device Cleaned (contains)"
            @input="debouncedSearch" clearable outlined dense hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.source_filename" label="Source Filename (contains)"
            @input="debouncedSearch" clearable outlined dense hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.job_id" label="Job ID (exact or contains)"
            @input="debouncedSearch" clearable outlined dense hide-details="auto"
          />
        </v-col>
        
        <v-col cols="12" class="mt-2">
          <v-select
            v-model="selectedCategories"
            :items="categories"
            label="Operation Category"
            multiple
            @change="debouncedSearch"
            hide-details="auto"
            dense
            outlined
          />
        </v-col>

        <v-col cols="12" md="4" class="d-flex align-center">
          <v-btn color="grey lighten-1" @click="clearFiltersAndShowInitial" block>Clear Filters</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Search Results -->
    <div v-if="isSearchReadyAndInitialized">
      <v-row v-if="isLoading">
        <v-col cols="12">
          <v-progress-linear indeterminate color="primary" class="mb-3" />
          <p class="text-center">Searching...</p>
        </v-col>
      </v-row>
      <div v-else>
        <v-alert v-if="searchPerformed && searchResults.length === 0 && hasActiveFilters" type="info" outlined class="mb-3">
          No records found matching your criteria.
        </v-alert>
        <v-data-table @click:row="onRowClick"
          :headers="tableHeaders"
          :items="searchResults"
          :items-per-page="25"
          item-key="id"
          class="elevation-1"
          :loading="isLoading"
          loading-text="Loading data..."
          no-data-text="No data to display. Try clearing filters or performing a search."
          dense
        >
          <template v-slot:top>
            <v-toolbar flat dense color="transparent">
              <v-toolbar-title>
                Showing {{ searchResults.length }}
                <span v-if="hasActiveFilters"> of {{ totalPossibleResults }} matching records</span>
                <span v-else> initial records (of {{ totalPossibleResults }} total)</span>
              </v-toolbar-title>
            </v-toolbar>
          </template>
        </v-data-table>
      </div>
    </div>
    <SwitchOrdersDetailsDialog :operationId="selectedOperationId" />
  </v-card-text>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, TreemapChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  // Tooltip formatter utility (if you uncomment the tooltip)
  // NOTE: formatUtil might not be directly exposed like this in newer ECharts.
  // You might need to implement formatting manually or use ECharts built-in features.
  // import { formatUtil } from 'echarts/lib/util/format'; // Check ECharts docs for current way
} from 'echarts/components'
import FlexSearch from 'flexsearch';
import pako from 'pako';
import VChart from 'vue-echarts'

// Register all components including TreemapChart
use([
  CanvasRenderer,
  BarChart,
  TreemapChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
])

import {
  getFirestore,
  collection,
  getDocs,
  getCountFromServer,
  query,
  orderBy,
  limit,
  startAfter,
  where
} from "firebase/firestore";
import debounce from "lodash/debounce";
import SwitchOrdersDetailsDialog from './SwitchOrdersDetailsDialog.vue';

const GZIPPED_JSON_URL = 'https://firebasestorage.googleapis.com/v0/b/hydro-ottawa-ai.appspot.com/o/operations%2Fswitchorders%2Fsearch_data_small.json.gz?alt=media&token=a317af07-22a0-4818-8755-7e4290721a17';

export default {
  name: 'SwitchOrdersDashboard',
  components: {
    VChart,
    SwitchOrdersDetailsDialog
  },
  data() {
    const categories = [
      'BLOWN/TRIP', 'CHECK', 'CLOSE', 'DELETED', 'EARTH/GROUND',
      'FLAG/FCI', 'HI-POT', 'MEGGER', 'OPEN', 'UNKNOWN'
    ];
    return {
      loading: false,
      headers: [
        { text: 'Device Name', value: 'device_cleaned', sortable: true },
        { text: 'Operation Type', value: 'operation_category', sortable: true },
        { text: 'Filename', value: 'source_filepath', sortable: true },
        { text: 'Job Number', value: 'job_id', sortable: true },
      ],
      operations: [],
      totalOperations: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      filters: {
        device: '',
        deviceProcessed: '',
        operation: '',
        source_filepath: '',
        job_id: ''
      },
      lastVisibleDocSnapshot: null,
      firstVisibleDocSnapshots: {},
      devicesChart: null, // Initialize as null
      operationsChart: null, // Initialize as null
      filters: {
        device_cleaned: '',
        source_filename: '',
        job_id: ''
      },
      categories,
      selectedCategories: [...categories],
      allFetchedData: [],
      searchResults: [],
      isLoading: false,
      searchPerformed: false,
      debounceTimer: null,
      totalPossibleResults: 0,
      searchIndexInstance: null,
      isSearchReadyAndInitialized: false,
      isInitializing: false,
      initializationStatus: 'Starting...',
      initializationError: null,
      recordsProcessed: 0,
      totalRecordsToProcess: 0,
      tableHeaders: [
        { text: 'Device Cleaned', value: 'device_cleaned' },
        { text: 'Operation Category', value: 'operation_category' },
        { text: 'Source Filename', value: 'source_filename' },
        { text: 'Job ID', value: 'job_id' }
      ],
      selectedOperationId: ""
    }
  },
  computed: {
    debouncedFetchData() {
      return debounce(this.fetchData, 500);
    },
    hasActiveFilters() {
      const textFilters = Object.values(this.filters).some(v => String(v).trim() !== '');
      const categoryFilter = this.selectedCategories.length !== this.categories.length;
      return textFilters || categoryFilter;
    }
  },
  watch: {
    tableOptions: {
      handler() {
        this.getOperations();
      },
      deep: true,
    },
    'filters.device'(newValue) {
      this.filters.deviceProcessed = newValue ? newValue.toLowerCase() : '';
      this.debouncedFetchData();
    }
  },
  async mounted() { // Make mounted async
    // Assign the result of the methods to the data properties
    this.devicesChart = await this.getDevicesChart();
    this.operationsChart = await this.getOperationsChart();
    this.getOperations(); // Fetch initial table data
    this.initializeSearchInternal();
  },
  methods: {
    fetchData() {
      this.tableOptions.page = 1;
      this.lastVisibleDocSnapshot = null;
      this.firstVisibleDocSnapshots = {};
      this.getOperations();
      // You might also want to refetch chart data if filters affect it
      this.updateCharts();
    },

    async updateCharts() {
        this.devicesChart = await this.getDevicesChart();
        this.operationsChart = await this.getOperationsChart();
        // If getDevicesChart/getOperationsChart fetch data based on filters,
        // ensure they use the current this.filters values.
    },

    async getOperations() {
        // ... (your existing getOperations Firestore logic - looks reasonable)
        if (this.loading) return;
        this.loading = true;
        this.operations = [];

        const db = getFirestore();
        const operationsRef = collection(db, 'op_switchorders_otos_operations');

        // --- Build Base Query with Filters ---
        let q = query(operationsRef);
        const activeFilters = [];
        if (this.filters.deviceProcessed) {
            activeFilters.push(where('device_cleaned', '>=', this.filters.deviceProcessed));
            activeFilters.push(where('device_cleaned', '<=', this.filters.deviceProcessed + '\uf8ff'));
        }
         if (this.filters.operation) {
           activeFilters.push(where('operation_category', '==', this.filters.operation));
         }
         if (this.filters.source_filepath) {
            activeFilters.push(where('source_filepath', '>=', this.filters.source_filepath));
            activeFilters.push(where('source_filepath', '<=', this.filters.source_filepath + '\uf8ff'));
         }
         if (this.filters.job_id) {
            // Assuming job_id is stored as a string. If it's a number, remove quotes.
           activeFilters.push(where('job_id', '==', this.filters.job_id));
         }

        if (activeFilters.length > 0) {
            q = query(operationsRef, ...activeFilters);
        } else {
            q = query(operationsRef);
        }

        // --- Get Total Count ---
        try {
          const countQuery = activeFilters.length > 0 ? query(operationsRef, ...activeFilters) : query(operationsRef);
          const countSnapshot = await getCountFromServer(countQuery);
          this.totalOperations = countSnapshot.data().count;
        } catch (error) {
          console.error("Error getting document count:", error);
          this.totalOperations = 0;
        }

        // --- Apply Sorting ---
        const sortBy = this.tableOptions.sortBy;
        const sortDesc = this.tableOptions.sortDesc;
        let orderedQuery = q;

        if (sortBy && sortBy.length > 0 && sortBy[0]) {
            const sortDirection = sortDesc[0] ? 'desc' : 'asc';
            // Ensure the first orderBy field is the one used in range filters if any
            // Example: If filtering device >= X and <= Y, the first orderBy MUST be device
            // Adjust this logic based on your most common filter/sort combinations
            // and Firestore indexing requirements.
             if (activeFilters.some(f => f._field.segments.join('/') === sortBy[0]) && activeFilters.some(f => ['>=', '<=', '>', '<'].includes(f._op))) {
                 // If sorting by a field used in a range filter, it must be the first orderBy
                 orderedQuery = query(orderedQuery, orderBy(sortBy[0], sortDirection));
             } else if (activeFilters.length > 0) {
                 // If filters are applied, but not sorting by a range-filtered field,
                 // Firestore might require the first orderBy to match an equality filter or inequality filter field.
                 // Let's keep the user's sort for now, but be aware of index needs.
                 // If errors occur, you might need a default sort matching an index.
                 orderedQuery = query(orderedQuery, orderBy(sortBy[0], sortDirection));
             } else {
                 // No filters, apply user sort directly
                 orderedQuery = query(orderedQuery, orderBy(sortBy[0], sortDirection));
             }

            // Add a secondary sort field (like a unique ID or timestamp) if the primary sort field
            // might have duplicate values. This ensures consistent pagination.
            // Example: orderedQuery = query(orderedQuery, orderBy('__name__')); // Sort by document ID as secondary
        } else {
            // Default sort order (important for pagination consistency)
             if (activeFilters.some(f => f._field.segments.join('/') === 'device_cleaned' && ['>=', '<='].includes(f._op))) {
                 orderedQuery = query(orderedQuery, orderBy('device_cleaned', 'asc')); // Match potential range filter
             } else {
                 orderedQuery = query(orderedQuery, orderBy('device_cleaned', 'asc')); // Default sort if no user sort
             }
             // Add secondary sort if needed: query(orderedQuery, orderBy('__name__'))
        }

        // --- Apply Pagination ---
        const itemsPerPage = this.tableOptions.itemsPerPage;
        const page = this.tableOptions.page;
        let paginatedQuery = orderedQuery;

        let startAfterDoc = null;
         // --- Calculate Start Document for Pagination ---
         // This simplified logic only handles moving forward page by page using lastVisibleDocSnapshot
         // or resetting to page 1. Handling jumping pages or going backward requires
         // caching snapshots per page or re-querying previous pages.
         if (page > 1 && this.lastVisibleDocSnapshot) {
             startAfterDoc = this.lastVisibleDocSnapshot;
             paginatedQuery = query(paginatedQuery, startAfter(startAfterDoc), limit(itemsPerPage));
         } else {
             // First page or missing snapshot
             this.lastVisibleDocSnapshot = null; // Reset if going back to page 1
             this.firstVisibleDocSnapshots = {};
             paginatedQuery = query(paginatedQuery, limit(itemsPerPage));
         }


        // --- Fetch Documents ---
        try {
          const querySnapshot = await getDocs(paginatedQuery);

          if (querySnapshot.docs.length > 0) {
              this.firstVisibleDocSnapshots[page] = querySnapshot.docs[0]; // Cache first doc of current page
              // Update last visible only if we fetched a full page or are on the last page
              if (querySnapshot.docs.length === itemsPerPage) {
                 this.lastVisibleDocSnapshot = querySnapshot.docs[querySnapshot.docs.length - 1];
              } else {
                 // Reached the end, clear the snapshot for the *next* page attempt
                 this.lastVisibleDocSnapshot = null;
              }
          } else {
              // No results found for this page
              this.lastVisibleDocSnapshot = null; // Clear snapshot if no docs found
          }

          this.operations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        } catch (error) {
          console.error("Error fetching operations:", error);
          // Check Firestore error messages, often related to missing indexes for composite queries/sorts
          this.operations = [];
          this.totalOperations = 0;
          this.lastVisibleDocSnapshot = null;
          this.firstVisibleDocSnapshots = {};
        } finally {
          this.loading = false;
        }
    },

    async getDevicesChart() {
      let data = [
        {
          "device_name": "CB1",
          "total_operations": 1528,
          "CHECK": 698,
          "OPEN": 539,
          "CLOSE": 280,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 1,
          "DELETED": 10,
          "UNKNOWN": 0
        },
        {
          "device_name": "Q2-Z",
          "total_operations": 702,
          "CHECK": 315,
          "OPEN": 197,
          "CLOSE": 190,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 0,
          "UNKNOWN": 0
        },
        {
          "device_name": "CB1-L",
          "total_operations": 641,
          "CHECK": 610,
          "OPEN": 14,
          "CLOSE": 14,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 3,
          "UNKNOWN": 0
        },
        {
          "device_name": "R1-F",
          "total_operations": 581,
          "CHECK": 190,
          "OPEN": 212,
          "CLOSE": 172,
          "EARTH/GROUND": 5,
          "BLOWN/TRIP": 0,
          "DELETED": 2,
          "UNKNOWN": 0
        },
        {
          "device_name": "Q1-Z",
          "total_operations": 543,
          "CHECK": 319,
          "OPEN": 116,
          "CLOSE": 108,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 0,
          "UNKNOWN": 0
        },
        {
          "device_name": "K1-X2",
          "total_operations": 540,
          "CHECK": 258,
          "OPEN": 143,
          "CLOSE": 139,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 0,
          "UNKNOWN": 0
        },
        {
          "device_name": "Q3-Z",
          "total_operations": 513,
          "CHECK": 279,
          "OPEN": 117,
          "CLOSE": 117,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 0,
          "UNKNOWN": 0
        },
        {
          "device_name": "624B1B2-CB",
          "total_operations": 405,
          "CHECK": 169,
          "OPEN": 118,
          "CLOSE": 117,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 1,
          "UNKNOWN": 0
        },
        {
          "device_name": "THQE-CB",
          "total_operations": 341,
          "CHECK": 156,
          "OPEN": 92,
          "CLOSE": 92,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 0,
          "DELETED": 1,
          "UNKNOWN": 0
        },
        {
          "device_name": "TKQE-CB",
          "total_operations": 329,
          "CHECK": 135,
          "OPEN": 94,
          "CLOSE": 95,
          "EARTH/GROUND": 0,
          "BLOWN/TRIP": 2,
          "DELETED": 2,
          "UNKNOWN": 1
        }
      ];
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            // Use axis to trigger tooltip
            type: 'shadow' // 'shadow' as default; can also be 'line' or 'shadow'
          }
        },
        legend: { show: false }, // Hidden but still takes up space
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: data.map(d => d.device_name)
        },
        series: [
          {
            name: 'OPEN',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d.OPEN)
          },
          {
            name: 'CHECK',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d.CHECK)
          },
          {
            name: 'CLOSE',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d.CLOSE)
          },
          {
            name: 'EARTH/GROUND',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d['EARTH/GROUND'])
          },
          {
            name: 'BLOWN/TRIP',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d['BLOWN/TRIP'])
          },
          {
            name: 'DELETED',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d['DELETED'])
          },
          {
            name: 'UNKNOWN',
            type: 'bar',
            stack: 'total',
            label: {
              show: true
            },
            emphasis: {
              focus: 'series'
            },
            data: data.map(d => d['UNKNOWN'])
          }
        ]
      }
    },

    // Placeholder for your second chart
    async getOperationsChart() {
      // Fetch Operation Metrics data from Firestore
      // const db = getFirestore();
      // const operationsRef = collection(db, 'op_switchorders_operation_metrics');
      // const querySnapshot = await getDocs(operationsRef);
      // const operationsData = querySnapshot.docs.map(doc => doc.data());

      // let treemapData = [];

      // operationsData.forEach(operation => {
      //   treemapData.push({
      //     name: operation.name,
      //     value: operation.count
      //   });
      // });

      // console.log('treemapData', treemapData);

      let treemapData = [
        { name: 'OPEN', value: 73493 },
        { name: 'CHECK', value: 119530 },
        { name: 'CLOSE', value: 77199 },
        { name: 'EARTH/GROUND', value: 3529 },
        { name: 'FLAG/FCI', value: 919 },
        { name: 'HI-POT', value: 356 },
        { name: 'MEGGER', value: 430 },
        { name: 'BLOWN/TRIP', value: 876 },
        { name: 'DELETED', value: 487 },
        { name: 'UNKNOWN', value: 20865 }
      ]

      return { // Return the options object
        tooltip: { // Basic tooltip is often useful
          formatter: function (info) {
            return `Operation: ${info.name}<br/>Count: ${info.value}`;
          }
        },
        series: [
          {
            name: 'Operations', // Series name
            type: 'treemap',
            visibleMin: 300, // Keep small items hidden if desired
            label: {
              show: true,
              formatter: '{b}' // Display the name and value inside the block
            },
            itemStyle: {
              borderColor: '#fff'
            },
            roam: 'none', // Allow only panning, not zooming
            levels: [ // Optional: Style different levels distinctly
              {
                itemStyle: {
                  borderWidth: 0,
                  gapWidth: 5
                }
              },
              {
                itemStyle: {
                  gapWidth: 1
                }
              },
              {
                colorSaturation: [0.35, 0.5],
                itemStyle: {
                  gapWidth: 1,
                  borderColorSaturation: 0.6
                }
              }
            ],
            data: treemapData
          }
        ]
      }
    },

    onRowClick(item) {
      this.selectedOperationId = item.id;
    },

    async initializeSearchInternal() {
      if (this.searchIndexInstance) {
        this.showInitialData();
        return;
      }
      this.isInitializing = true;
      try {
        const resp = await fetch(GZIPPED_JSON_URL);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const buf = new Uint8Array(await resp.arrayBuffer());
        let text;
        if (buf[0] === 0x1f && buf[1] === 0x8b) {
          text = pako.ungzip(buf, { to: 'string' });
        } else {
          text = new TextDecoder('utf-8').decode(buf);
        }
        const data = JSON.parse(text);
        this.allFetchedData = data;
        this.totalRecordsToProcess = data.length;
        const idx = new FlexSearch.Document({
          charset: 'latin:advanced', language: 'en',
          document: { id: 'id', index: ['device_cleaned','operation_category','source_filename','job_id'], store: true }
        });
        const chunkSize = 10000;
        for (let i = 0; i < data.length; i += chunkSize) {
          data.slice(i, i + chunkSize).forEach(r => idx.add(r));
          this.recordsProcessed = Math.min(i + chunkSize, data.length);
          await new Promise(res => setTimeout(res, 0));
        }
        this.searchIndexInstance = idx;
        this.isSearchReadyAndInitialized = true;
        this.showInitialData();
      } catch (err) {
        console.error('[INIT] error', err);
        this.initializationError = err.message;
      } finally {
        this.isInitializing = false;
      }
    },
    showInitialData() {
      this.searchResults = [...this.allFetchedData];
      this.totalPossibleResults = this.allFetchedData.length;
      this.searchPerformed = false;
      this.isLoading = false;
    },
    async performSearch() {
      if (!this.searchIndexInstance) return;
      this.isLoading = true;
      this.searchPerformed = true;
      // Build text queries
      const textQueries = Object.entries(this.filters)
        .filter(([_, v]) => String(v).trim())
        .map(([field, query]) => ({ field, query: String(query).trim().toLowerCase() }));
      // FlexSearch for text fields
      let raw = [];
      if (textQueries.length) {
        try {
          raw = await this.searchIndexInstance.searchAsync({
            enrich: true, bool: 'and', limit: 10000, index: textQueries
          });
        } catch (e) {
          console.error('[SEARCH] FlexSearch error:', e);
        }
      } else {
        // no text filters means start from all
        raw = this.allFetchedData.map(item => ({ result: [item] }));
      }
      // Flatten results
      const items = [];
      raw.forEach(set => {
        if (set.result) items.push(...set.result);
        else items.push(set);
      });
      const records = items.map(it => it.doc || it);
      // Filter by category
      const catFilter = rec => this.selectedCategories.includes(rec.operation_category);
      // Manual AND on text filters
      const finalHits = records.filter(rec => {
        const matchesText = textQueries.every(q =>
          String(rec[q.field] || '').toLowerCase().includes(q.query)
        );
        return matchesText && catFilter(rec);
      });
      // Deduplicate
      const unique = [];
      const seen = new Set();
      finalHits.forEach(r => {
        if (!seen.has(r.id)) { seen.add(r.id); unique.push(r); }
      });
      this.searchResults = unique;
      this.totalPossibleResults = unique.length;
      this.isLoading = false;
    },
    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.performSearch(), 400);
    },
    clearFiltersAndShowInitial() {
      Object.keys(this.filters).forEach(k => (this.filters[k] = ''));
      this.selectedCategories = [...this.categories];
      this.showInitialData();
    }

  }
}
</script>

<style lang="scss" scoped>
/* Add styles if needed */
.chart {
  height: 400px; /* Give charts a default height */
  margin-bottom: 20px;
}
</style>